import { HealthAndSafetyOutlined } from "@mui/icons-material";
import {
  Box,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Coverage } from "fhir/r5";

import {
  formatInsuranceCardNumber,
  getHealthCardGradient,
  getInsurerInitials,
} from "./healthInsuranceCardTheme";

export type HealthInsuranceCardLayout = "web" | "mobile";

interface HealthInsuranceCardFaceProps {
  coverage: Coverage;
  colorIndex?: number;
  /** web: cartão contido (~320px); mobile: largura total do container */
  layout?: HealthInsuranceCardLayout;
  /** Cantos superiores arredondados quando embutido em card com ações */
  embedded?: boolean;
}

const CARD_ASPECT = "1.586 / 1";

export function HealthInsuranceCardFace({
  coverage,
  colorIndex = 0,
  layout = "web",
  embedded = false,
}: HealthInsuranceCardFaceProps) {
  const theme = useTheme();
  const isMobileLayout = layout === "mobile";
  const cardNumber = coverage.identifier?.[0]?.value || "";
  const insurerName = coverage.insurer?.display || "Operadora";
  const planName = coverage.insurancePlan?.display || "Plano de saúde";
  const isActive = (coverage.status || "active") === "active";
  const gradient = getHealthCardGradient(theme, colorIndex);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: isMobileLayout ? "100%" : 320,
        mx: isMobileLayout ? 0 : "auto",
        aspectRatio: CARD_ASPECT,
        borderRadius: embedded
          ? "12px 12px 0 0"
          : isMobileLayout
            ? 2.5
            : 2.75,
        overflow: "hidden",
        background: isActive
          ? gradient
          : `linear-gradient(135deg, ${theme.palette.neutral600.main} 0%, ${theme.palette.neutral800.main} 100%)`,
        boxShadow: embedded
          ? "none"
          : isActive
            ? "0 8px 22px rgba(15, 23, 42, 0.16)"
            : "0 4px 14px rgba(15, 23, 42, 0.1)",
        color: "#fff",
        isolation: "isolate",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.2,
          background: `
            radial-gradient(circle at 85% 15%, rgba(255,255,255,0.4) 0%, transparent 42%),
            radial-gradient(circle at 10% 90%, rgba(255,255,255,0.15) 0%, transparent 38%)
          `,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: isMobileLayout ? -12 : -20,
          bottom: isMobileLayout ? -24 : -32,
          width: isMobileLayout ? 88 : 120,
          height: isMobileLayout ? 88 : 120,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: alpha("#fff", 0.1),
          pointerEvents: "none",
        }}
      />

      <Stack
        sx={{
          position: "relative",
          height: "100%",
          p: isMobileLayout ? 1.5 : 1.65,
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={0.5}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: isMobileLayout ? 30 : 32,
                height: isMobileLayout ? 30 : 32,
                borderRadius: 1.5,
                bgcolor: alpha("#fff", 0.18),
                border: "1px solid",
                borderColor: alpha("#fff", 0.28),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "0.625rem",
                fontWeight: 800,
              }}
            >
              {insurerName ? (
                getInsurerInitials(insurerName)
              ) : (
                <HealthAndSafetyOutlined sx={{ fontSize: 16 }} />
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: "0.625rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                opacity: 0.92,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Cartão de saúde
            </Typography>
          </Stack>

          <Chip
            size="small"
            label={isActive ? "Ativo" : "Inativo"}
            sx={{
              height: 22,
              fontSize: "0.625rem",
              fontWeight: 800,
              bgcolor: alpha("#fff", isActive ? 0.22 : 0.12),
              color: "#fff",
              border: "1px solid",
              borderColor: alpha("#fff", 0.25),
              flexShrink: 0,
            }}
          />
        </Stack>

        <Box sx={{ py: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              opacity: 0.78,
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              mb: 0.25,
            }}
          >
            Número da carteirinha
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Roboto Mono', monospace",
              fontWeight: 600,
              fontSize: isMobileLayout ? "0.875rem" : "0.875rem",
              letterSpacing: "0.06em",
              lineHeight: 1.35,
              wordBreak: "break-word",
            }}
          >
            {formatInsuranceCardNumber(cardNumber)}
          </Typography>
        </Box>

        <Stack spacing={0.2} minWidth={0}>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.75,
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {insurerName}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              fontSize: "0.8125rem",
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {planName}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
